import {FC} from "react";
import {useTranslation} from "react-i18next";
import {useMachine} from "react-robot";
import {BackendError} from "@common-core/runtime-js/backend";
import {
  CreateOperationalEntityData,
  CreateOperationalEntityWithTenantInput,
  OperationalEntity
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {useGraphqlMutation} from "../../../amplify/hooks";
import {TriggerResult} from "../../../amplify/hooks/types";
import {
  createOperationalEntityWithTenant as createOperationalEntityWithTenantMutation,
  createTenant as createTenantMutation
} from "../../../amplify/graphql";
import {useFeedback} from "../../../feedback";
import {
  CreateOperationalEntityWithTenantData,
  CreateOperationalEntityWithTenantVariables,
  CreateTenantResponse,
  CreateTenantVariables
} from "../../backend";
import {usePlan} from "./context";
import {CreatePlan} from "./view-model";
import {toCreateResult, toStateMachineEvent} from "./transforms";
import {formatTenantCreationError} from "./helpers";
import {stateMachine} from "./state-machine";
import "./OrganizationBuilder.scss";

export const OrganizationBuilder: FC = () => {
  const {t} = useTranslation();
  const [, setFeedback] = useFeedback();
  const {plan} = usePlan();

  const [createOperationalEntityWithTenant] = useGraphqlMutation<
    CreateOperationalEntityWithTenantData,
    CreateOperationalEntityWithTenantVariables
  >({
    amplifyMutation: createOperationalEntityWithTenantMutation
  });

  const [createTenant] = useGraphqlMutation<
    CreateTenantResponse,
    CreateTenantVariables
  >({
    amplifyMutation: createTenantMutation
  });

  const assertResultPresentWithTenant = (
    result: TriggerResult<CreateOperationalEntityWithTenantData>
  ): CreateOperationalEntityData => {
    if (!result.data?.createOEResult) {
      throw new BackendError(t("create-organization.errors.no-results"), 500);
    }
    return result.data.createOEResult;
  };

  const sendFailureEventToStateMachine = (error: Error) => {
    // GraphQL error occurred - send failure event with minimal failed result
    send({
      type: "failure",
      result: {
        sources: plan.newEntity.sources.map(s => ({
          sourceId: s.sourceId,
          sourceType: s.sourceType
        })),
        dbaName: plan.newEntity.organizationInformation.dbaName,
        status: "failed",
        message: error.message || "Creation failed",
        children: []
      },
      source: plan.source
    });
  };

  /**
   * Extracts the ultimate parent name from the plan's source hierarchy
   * @throws Error if hierarchy or custName is not present
   */
  const getUltimateParentName = (plan: CreatePlan): string => {
    if (!plan.source.hierarchy?.custName) {
      throw new Error(
        "Cannot determine ultimate parent name: hierarchy.custName is missing"
      );
    }
    return plan.source.hierarchy.custName;
  };

  const ultimateParentName = getUltimateParentName(plan);

  /**
   * Creates a tenant if required by the plan (newEntity.tenantId is undefined)
   *
   * @returns The created tenant ID if successful, or undefined if no tenant creation was needed or creation failed
   *
   * Possible errors:
   * - Missing tenant ID in response: Tenant API returns success but no ID
   * - GraphQL errors upon creation attempt: Service unavailable, network timeout, or backend errors
   *
   * All errors are displayed as a Feedback popup, and a failure event is sent to the state machine.
   */
  const createTenantIfRequired = async (): Promise<string | undefined> => {
    if (plan.newEntity.tenantId !== undefined) {
      return undefined;
    }

    try {
      const tenantResult = await createTenant({
        variables: {
          input: {
            name: ultimateParentName
          }
        }
      });

      // Check if tenant creation returned a valid ID
      if (!tenantResult.data?.createTenant?.tenant?.id) {
        const noIdError = new Error(
          t("create-organization.errors.tenant.no-tenant-id-detail")
        );

        // State machine will handle feedback display
        sendFailureEventToStateMachine(noIdError);
        return undefined;
      }

      // Happy path return:
      return tenantResult.data.createTenant.tenant.id;
    } catch (tenantError) {
      const formattedError = formatTenantCreationError(
        tenantError as Error,
        ultimateParentName,
        t
      );

      // State machine will handle feedback display
      sendFailureEventToStateMachine(formattedError);
      return undefined;
    }
  };

  const createOrganization = async (
    newEntity: OperationalEntity
  ): Promise<void> => {
    try {
      const createdTenantId = await createTenantIfRequired();

      if (plan.newEntity.tenantId === undefined && !createdTenantId) {
        return; // Error already handled by createTenantIfRequired
      }

      //Convert newEntity into a CreateOperationalEntityWithTenantInput object.
      const {
        id,
        isComplex,
        parent,
        boidAssignments,
        enterpriseEntitlements,
        enterpriseEntitlementsV2,
        statuses,
        ...validInputFields
      } = newEntity;

      const finalInput: CreateOperationalEntityWithTenantInput = {
        ...validInputFields,
        tenantId: createdTenantId || newEntity.tenantId
      };

      return createOperationalEntityWithTenant({
        variables: {
          createOEInput: finalInput
        }
      })
        .then(assertResultPresentWithTenant)
        .then(toCreateResult)
        .then(result => ({...result, tenantId: createdTenantId ?? ""}))
        .then(result => {
          const event = toStateMachineEvent(plan, result);
          if (event) send(event);
        })
        .catch(oeError => {
          // Handle OE creation failure after successful tenant creation
          if (createdTenantId) {
            const partialFailureError = new Error(
              t(
                "create-organization.errors.oe.creation-failed-after-tenant-detail",
                {
                  tenantId: createdTenantId
                }
              )
            );

            // State machine will handle feedback display
            sendFailureEventToStateMachine(partialFailureError);
          } else {
            sendFailureEventToStateMachine(oeError);
          }
        });
    } catch (error) {
      // Catch any unexpected errors
      const unexpectedError = error as Error;
      // State machine will handle feedback display
      sendFailureEventToStateMachine(unexpectedError);
    }
  };

  const [{context}, send] = useMachine(stateMachine, {
    createOrganization,
    translate: t,
    showFeedback: setFeedback,
    clearFeedback: () => setFeedback(undefined),
    create: () => send({type: "create", ...plan}),
    retry: () => send({type: "retry", ...plan}),
    stepBack: () => send({type: "step-back"})
  } as any);

  return (
    <section className="organization-builder">
      {context.stepper()}
      {context.step()}
      {context.controls()}
    </section>
  );
};
